import type { User } from "features/Users/types/user.types";

/** Department / designation row from API includes */
export interface EmployeeOrgRef {
  id: number;
  name: string;
}

/**
 * Employee entity (camelCase). API may send snake_case — use
 * `normalizeEmployee` from `services/employeeNormalize.ts` when reading responses.
 */
export interface Employee {
  id: number;
  userId: number;
  code: string;
  /** ISO string from API (Sequelize serializes Date as JSON string) */
  joinDate: string;
  /** Legacy mirrored fields; canonical source is `user`. */
  firstName: string;
  /** Legacy mirrored fields; canonical source is `user`. */
  lastName: string;
  /** Legacy mirrored fields; canonical source is `user`. */
  email: string;
  /** Legacy mirrored fields; canonical source is `user`. */
  phone: string | null;
  departmentId: number | null;
  designationId: number | null;
  employmentCategoryId: number | null;
  avatar?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  bloodGroup?: string | null;
  department?: EmployeeOrgRef | null;
  designation?: EmployeeOrgRef | null;
  employmentCategory?: EmployeeOrgRef | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  reportsToEmployeeId?: number | null;
  isPayrollable?: boolean;
  manager?: {
    id: number;
    userId: number;
    code?: string;
    user?: User | null;
  } | null;
  directReports?: Array<{
    id: number;
    userId: number;
    code?: string;
    user?: User | null;
  }> | null;
  user?: User | null;
}

export interface AddEmployeeRequest {
  /** Required in link-existing mode; omit in create-new mode. */
  userId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  departmentId?: number | null;
  designationId?: number | null;
  employmentCategoryId: number;
  joinDate: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  bloodGroup?: string | null;
  avatar?: string | null;
}

export interface UpdateEmployeeRequest extends AddEmployeeRequest {
  id: number;
}

/** Accepts `Employee` or raw nested `user.employee` from the API. */
export function departmentDisplayName(emp: {
  department?: EmployeeOrgRef | string | null;
  departmentId?: number | null;
  department_id?: number | null;
}): string {
  const d = emp.department;
  if (d && typeof d === "object" && "name" in d && d.name) return d.name;
  if (typeof d === "string" && d.trim()) return d.trim();
  const id = emp.departmentId ?? emp.department_id;
  if (id != null) return `Department #${id}`;
  return "—";
}

export function designationDisplayName(emp: {
  designation?: EmployeeOrgRef | string | null;
  designationId?: number | null;
  designation_id?: number | null;
}): string {
  const d = emp.designation;
  if (d && typeof d === "object" && "name" in d && d.name) return d.name;
  if (typeof d === "string" && d.trim()) return d.trim();
  const id = emp.designationId ?? emp.designation_id;
  if (id != null) return `Designation #${id}`;
  return "—";
}

export function employmentCategoryDisplayName(emp: {
  employmentCategory?: EmployeeOrgRef | string | null;
  employmentCategoryId?: number | null;
  employment_category_id?: number | null;
}): string {
  const c = emp.employmentCategory;
  if (c && typeof c === "object" && "name" in c && c.name) return c.name;
  if (typeof c === "string" && c.trim()) return c.trim();
  const id = emp.employmentCategoryId ?? emp.employment_category_id;
  if (id != null) return `Employment category #${id}`;
  return "—";
}
